import type { ReactNode } from "react";
import { Link } from "react-router-dom";

interface AdminShellProps {
  title: string;
  subtitle: string;
  active: "security" | "seo";
  children: ReactNode;
}

const links: { key: "security" | "seo"; label: string; href: string; icon: string }[] = [
  { key: "security", label: "Security", href: "/admin/security", icon: "ri-shield-keyhole-line" },
  { key: "seo", label: "SEO Report", href: "/admin/seo", icon: "ri-line-chart-line" },
];

/** Minimal, self-contained admin chrome shared by the internal tools pages. */
export default function AdminShell({ title, subtitle, active, children }: AdminShellProps) {
  const year = new Date().getFullYear();

  return (
    <div className="flex min-h-screen flex-col bg-background-100">
      <header className="border-b border-background-200 bg-background-50">
        <div className="mx-auto flex max-w-screen-2xl flex-col gap-4 px-4 py-4 md:flex-row md:items-center md:justify-between md:px-6">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-shield-check-line text-xl" aria-hidden="true" />
            </span>
            <div>
              <p className="font-heading text-sm font-bold text-foreground-950">Lahari Admin</p>
              <p className="text-xs text-foreground-500">Internal tools</p>
            </div>
          </div>
          <nav className="flex flex-wrap items-center gap-2">
            {links.map((link) => (
              <Link
                key={link.key}
                to={link.href}
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                  active === link.key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:bg-background-200"
                }`}
              >
                <i className={link.icon} aria-hidden="true" /> {link.label}
              </Link>
            ))}
            <Link
              to="/"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <i className="ri-external-link-line" aria-hidden="true" /> View site
            </Link>
          </nav>
        </div>
      </header>

      <main className="mx-auto w-full max-w-screen-2xl flex-1 px-4 py-8 md:px-6">
        <div className="mb-6">
          <h1 className="font-heading text-2xl font-bold text-foreground-950">{title}</h1>
          <p className="mt-1 text-sm text-foreground-600">{subtitle}</p>
        </div>
        {children}
      </main>

      <footer className="border-t border-background-200 bg-background-50 py-4 text-center text-xs text-foreground-500">
        © {year} Lahari Packers and Movers · Internal admin tools
      </footer>
    </div>
  );
}